import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { graphql, withPrefix } from "gatsby";
import Layout from "../components/layout";
import Seo from "../components/seo";
import Breadcrumb from "../components/breadcrumb";
import TechnicalSpecifications from "../components/technical-specifications";
import ItemsGrid from "../components/items-grid";
import SliderRelated from "../components/slider-related";
import ScrollTabs from "../components/scroll-tabs";
import ProductDetailTop from "../components/product-detail-top";
import CompareBar from "../components/compare-bar";
import ReevooEmbeddable from "../components/reevoo-embeddable";
import DocumentazioneProdotto from "../components/documentazione-prodotto";
import BannerPromo from "../components/banner-promo";
import JsonLd from "../components/json-ld";
import { CompareProvider } from "../context/compare";
import BannerPushNotification from "../components/elements/banner-push-notification";
import ProductDetailBottom from "../components/product-detail-bottom";
import ProductNotes from "../components/elements/product-notes";
import useScrolled from "../hooks/use-scrolled";
import useScrollTabs from "../hooks/use-scroll-tabs";
import useEnergyClassImg from "../hooks/use-energy-class-img";
import { productDetailView } from "../utility/datalayer";
import { getCampaignBoxes } from "../utility/campaigns";
import { queryProductsDetails } from "../utility/ecommerce";
import { normalizeRelativePath } from "../utility/paths";
import safeSlugify from "../utility/safe-slugify";
import { CDN_BASE_URL } from "../utility/constants";
import babelConfig from "../../babel-config.json";
import placeholderLogoImg from "../images/logo_haier_search.jpg";
import AgecRegulation from "../components/agec-regulation";
import BannerPushApp from "../components/banner-push-app";
import { hasEcommerce } from "../utility/ecommerce";
import useNoRtl from "../hooks/use-no-rtl";
import BabelProdottoIntro from "../components/babel-prodotto-intro";
import useEkyScript from "../hooks/use-eky-script";
import { getDDLPrice } from "../utility/get-ddl-price";

const FAMILY_IMAGE_BASE_URL = `${CDN_BASE_URL}/products/families-and-triples`;
const formatImageUrl = (imageUrl) =>
  imageUrl
    ? imageUrl.startsWith("http")
      ? imageUrl
      : `${FAMILY_IMAGE_BASE_URL}/${imageUrl}`
    : null;
const formatLogoUrl = (logoUrl) =>
  logoUrl
    ? logoUrl.startsWith("http")
      ? logoUrl
      : !logoUrl.includes(".")
      ? `${FAMILY_IMAGE_BASE_URL}/icon-svg/${logoUrl}.svg`
      : `${FAMILY_IMAGE_BASE_URL}/icon-svg/${logoUrl}`
    : null;

const calculateItemsGrid = (productFamily, triples, labelManagerLabels) => {
  return {
    moduleTitleFieldSet: (productFamily?.title && (productFamily?.text || productFamily?.image)
      ? [
          {
            anchorDesktop: null,
            moduleLook: ["Inline"],
            sectionTitleFieldSet: [
              {
                sectionTitle: productFamily.title,
                isFamily: true,
                sectionText: productFamily.text,
                sectionImageFieldSet: productFamily.imageAdaptive
                  ? [
                      {
                        sectionImage: {
                          node: productFamily.imageAdaptive,
                          nodeFull: productFamily.imageAdaptiveFull,
                        },
                        altImage: productFamily.titleimg,
                      },
                    ]
                  : null,
                video: productFamily.video,
                labelFieldSet: productFamily.linkcall
                  ? {
                      label: labelManagerLabels.discoverMore,
                      link: productFamily.linkcall,
                      color: [""],
                    }
                  : null,
                logoImage: productFamily?.logo_image
                  ? [
                      {
                        node: formatImageUrl(productFamily.logo_image),
                        altImage: productFamily.titleimg,
                      },
                    ]
                  : null,
              },
            ],
          },
        ]
      : []
    ).concat({
      anchorDesktop: null,
      moduleLook: ["?"],
      sectionTitleFieldSet: (productFamily ? productFamily.triples.concat(triples) : triples)
        .sort((a, b) => {
          const orderA = parseInt(a.order);
          const orderB = parseInt(b.order);
          if (orderA !== orderB) {
            return orderB - orderA;
          }
          return b.id - a.id;
        })
        .map((item) => ({
          isFamily: false,
          sectionTitle: item.title,
          sectionText: item.text,
          sectionImageFieldSet: [
            {
              sectionImage: {
                node: item.imageAdaptive,
                nodeFull: item.imageAdaptiveFull,
              },
              altImage: item.titimg,
            },
          ],
          iconBabel: formatLogoUrl(item.logoSvg),
          video: item.video,
          labelFieldSet: item.linkcall
            ? {
                label: labelManagerLabels.discoverMore,
                link: item.linkcall,
                color: ["Black"],
              }
            : null,
        })),
    }),
  };
};

const generateMetadata = (babelProdotto, priceData, config) => {
  const {
    key_prod,
    productName,
    eanCode,
    primaryimage,
    familiesIds,
    productDescBefore,
    productDescAfter,
    minicard4,
    pageLine,
    productEcommerceSellingPrice,
    ratingValue,
    reviewCount,
  } = babelProdotto;
  const primaryImg = primaryimage.find((img) => img.imageType === "640");
  const productFamily = familiesIds && familiesIds.length ? familiesIds[0] : null;
  const pageLineNames = pageLine?.pageName;
  const lineProductName2 = pageLineNames && pageLineNames[1] ? pageLineNames[1] : "";
  const miniCardData = lineProductName2 ? [lineProductName2].concat(minicard4) : minicard4;
  const metaDescription =
    (productDescBefore ? productDescBefore : miniCardData.slice(0, 8).join(" ")) +
    " " +
    (productDescAfter ? productDescAfter : miniCardData.slice(8).join(" "));
  return {
    "@context": "http://schema.org/",
    "description": productFamily ? productFamily.text : metaDescription,
    "@type": "Product",
    "name": [productFamily?.title, productName].filter(Boolean).join(" | "),
    "brand": {
      "@type": "Brand",
      "name": "Haier",
    },
    "image": primaryImg
      ? primaryImg.imageUrl
      : `${process.env.GATSBY_SITE_URL}${withPrefix(placeholderLogoImg)}`,
    "@id": `#reviewable_sku_${key_prod}`,
    "mpn": eanCode,
    "sku": key_prod,
    ...(ratingValue && reviewCount
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            "ratingValue": ratingValue ? ratingValue : null,
            "bestRating": "5",
            "worstRating": "0",
            "reviewCount": reviewCount ? reviewCount : null,
          },
        }
      : null),
    ...((hasEcommerce && productEcommerceSellingPrice) || priceData?.sellingPrice?.value
      ? {
          offers: {
            "@type": "Offer",
            "price": productEcommerceSellingPrice
              ? Number(productEcommerceSellingPrice).toString()
              : priceData
              ? Number(priceData.sellingPrice?.value).toString()
              : undefined,
            ...(priceData !== undefined && {
              availability: priceData
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
            }),
            "priceCurrency": productEcommerceSellingPrice
              ? config?.currency
              : priceData
              ? priceData.sellingPrice.currency
              : undefined,
          },
        }
      : null),
  };
};

const ProductDetailTemplate = ({
  data: {
    babelProdotto,
    config,
    site: { siteMetadata },
    campaignsGlobalBannerTop,
    liferayProductNotes,
    agecRegulation,
    labelManagerLabels,
    newMainMenuFiltered,
    liferayMenuConfiguration,
  },
  location,
}) => {
  const {
    key_prod: productId,
    lineId,
    productName,
    productCode,
    energyClass,
    familiesIds,
    triples,
    eanCode,
    minicard4,
    pageLine,
    brandName,
    promoPdp,
    featuresPdp,
    related,
    extraInfo,
    productDescAfter,
    campaignsBannerPieDiPaginaPDP,
    campaignsBannerTop,
    productApp,
    attributesMap,
    repairabilityIndex,
    declarationOfConformity,
    manuals,
    productTitleNoFamilyAC,
    slug,
  } = babelProdotto;

  const {
    catalogueLinkFieldSet,
    isProductSheet,
    isNewEnergyLabelEu,
    isFicheDeProduit,
    isNewFicheDeProduit,
    isNewEnergyLabelUk,
    isDeclarationOfConformity,
    isDeclarationOfConformityUk,
    isNotEcommerce,
  } = babelProdotto?.pageLine?.lineConfigurationFieldSet;

  const enabledForLine = [undefined, "[]"].includes(isNotEcommerce?.[0]);

  const LANG = useMemo(
    () => siteMetadata.derivedLang || siteMetadata.priceLanguage.split("-")[0].toUpperCase(),
    [siteMetadata]
  );

  const manual = useMemo(
    () =>
      manuals &&
      manuals.find(
        (item) =>
          item.lang === LANG && [0, 50700].includes(item.type) && item.filename?.startsWith("http")
      ),
    [manuals, LANG]
  );

  const showCatalogueLink =
    catalogueLinkFieldSet &&
    catalogueLinkFieldSet.catalogueLink &&
    !!catalogueLinkFieldSet.isCatalogue?.length
      ? true
      : false;
  const showProductSheet = isProductSheet && !!isProductSheet?.length ? true : false;
  const showNewEnergyLabelEu = isNewEnergyLabelEu && !!isNewEnergyLabelEu?.length ? true : false;
  const showFicheDeProduit = isFicheDeProduit && !!isFicheDeProduit?.length ? true : false;
  const showNewFicheDeProduit = isNewFicheDeProduit && !!isNewFicheDeProduit?.length ? true : false;
  const showNewEnergyLabelUk = isNewEnergyLabelUk && !!isNewEnergyLabelUk?.length ? true : false;
  const showDeclarationOfConformity =
    isDeclarationOfConformity &&
    isDeclarationOfConformity &&
    !!isDeclarationOfConformity?.length &&
    declarationOfConformity?.eu === "true"
      ? true
      : false;
  const showDeclarationOfConformityUk =
    isDeclarationOfConformityUk &&
    isDeclarationOfConformity &&
    !!isDeclarationOfConformityUk?.length &&
    declarationOfConformity?.uk === "true"
      ? true
      : false;
  const showMauals = manual ? true : false;

  const { origin } = location.state || {};

  const noRtl = useNoRtl("pdp");

  const isNewMenu =
    !!newMainMenuFiltered &&
    newMainMenuFiltered?.name &&
    liferayMenuConfiguration?.newMenuActive &&
    liferayMenuConfiguration?.copyrightText !== "";

  const breadcrumb = useMemo(() => {
    const { parentPages } = pageLine;
    return [
      {
        name: labelManagerLabels.home,
        link: "/",
      },
    ].concat(
      parentPages.map((item) => ({
        data: item.page,
        name:
          isNewMenu && item.menuItem?.useCustomName && item.menuItem?.name
            ? item.menuItem?.name
            : item.page.liferayFields?.title,
        link: item.page.slug
          ? normalizeRelativePath(safeSlugify(item.page.slug))
          : item.page.externalLink || "",
      }))
    );
  }, [pageLine, labelManagerLabels, isNewMenu]);
  const lineName = pageLine.comparePage.slug;
  const [lineProductName] = pageLine.pageName;
  //TODO serve davvero?

  const pathPrefix = withPrefix("/");

  const productFamily = familiesIds && familiesIds.length ? familiesIds[0] : null;
  const pageLineNames = pageLine.pageName;
  const lineProductName2 =
    pageLineNames && pageLineNames[1] && pageLineNames[1] ? pageLineNames[1] : "";

  const invertProductFamily =
    config?.invertProductFamily === "yes" ||
    babelConfig.invertProductFamilyPaths.includes(pathPrefix);

  const productTitle =
    lineId === 333 && !(familiesIds?.length > 0) && productTitleNoFamilyAC
      ? productTitleNoFamilyAC
      : productFamily && invertProductFamily
      ? `${productFamily.title} ${lineProductName}`
      : productFamily && !invertProductFamily
      ? `${lineProductName} ${productFamily.title}`
      : lineProductName;

  const itemsGrid = useMemo(
    () => calculateItemsGrid(productFamily, triples, labelManagerLabels),
    [productFamily, triples, labelManagerLabels]
  );

  const itemsGridModules =
    itemsGrid?.moduleTitleFieldSet?.length > 0 &&
    itemsGrid?.moduleTitleFieldSet
      ?.map((item) => {
        return item?.sectionTitleFieldSet || [];
      })
      .flat();

  const showLogoSvg =
    (itemsGridModules?.length > 0 &&
      (itemsGridModules?.find((element) => !element?.isFamily && !element?.iconBabel) ||
        itemsGridModules?.find(
          (element) =>
            !element?.isFamily &&
            element?.sectionImageFieldSet?.length > 0 &&
            (element?.sectionImageFieldSet?.sectionImage?.node ||
              element?.sectionImageFieldSet?.sectionImage?.nodeFull)
        ))) ||
    !itemsGridModules?.length
      ? false
      : true;
  const showLogoImage =
    itemsGridModules?.length > 0 &&
    itemsGridModules?.find(
      (element) =>
        element?.logoImage &&
        !element?.sectionImageFieldSet?.length > 0 &&
        !(
          element?.sectionImageFieldSet?.sectionImage?.node ||
          element?.sectionImageFieldSet?.sectionImage?.nodeFull
        ) &&
        !!element?.isFamily
    )
      ? true
      : false;

  const compareProducts = pageLine.allBabelProdotto;

  const [priceData, setPriceData] = useState(() => getDDLPrice(babelProdotto, config));

  const bannerRef = useRef();
  const scrolledBannerRef = useScrolled(bannerRef);

  const specificationsFiltered = babelProdotto.specifications
    .filter((item) => item.category.id !== 1068)
    .sort((item) => item.category.ordCategory);

  const categoriesFiltered = babelProdotto.agecCategories;

  const tabs = useMemo(() => {
    return []
      .concat(
        itemsGrid?.moduleTitleFieldSet?.length > 0 &&
          itemsGrid?.moduleTitleFieldSet?.find((item) => item?.sectionTitleFieldSet?.length)
          ? {
              id: "introduction",
              label: labelManagerLabels.introduction,
            }
          : []
      )
      .concat(
        specificationsFiltered
          ? {
              id: "technical-spec",
              label: labelManagerLabels.technicalSpecifications,
            }
          : []
      )
      .concat(
        categoriesFiltered.length > 0 && agecRegulation.anchorDesktop
          ? {
              id: "agec-regulation",
              label: agecRegulation.anchorDesktop,
            }
          : []
      )
      .concat(
        showMauals ||
          showDeclarationOfConformityUk ||
          showDeclarationOfConformity ||
          showNewEnergyLabelUk ||
          showFicheDeProduit ||
          showNewFicheDeProduit ||
          showNewEnergyLabelEu ||
          showProductSheet ||
          showCatalogueLink
          ? {
              id: "documentation",
              label: labelManagerLabels.documents,
            }
          : []
      )
      .concat(
        related && related.length >= 3 && !noRtl
          ? {
              id: "related",
              label: labelManagerLabels.relatedProducts,
            }
          : []
      )
      .concat(
        !!config.isReevooActiveFieldSet.isReevooActive?.length
          ? {
              id: "reviews",
              label: labelManagerLabels.reviews,
            }
          : []
      )
      .concat(productApp?.anchorDesktop ? { id: "h-on-app", label: productApp.anchorDesktop } : []);
  }, [
    config,
    related,
    labelManagerLabels,
    agecRegulation,
    categoriesFiltered,
    productApp,
    noRtl,
    specificationsFiltered,
    itemsGrid,
    showCatalogueLink,
    showDeclarationOfConformity,
    showDeclarationOfConformityUk,
    showFicheDeProduit,
    showNewFicheDeProduit,
    showMauals,
    showNewEnergyLabelEu,
    showNewEnergyLabelUk,
    showProductSheet,
  ]);
  const sections = useMemo(() => tabs.map((tab) => tab.id), [tabs]);

  const [activeTab, tabsRef, tabsHeight] = useScrollTabs(sections);
  const [scrolledHeight, setScrolledHeight] = useState();
  const [scrolledBannerHeight, setScrolledBannerHeight] = useState();
  const [scrolledMobile, setScrolledMobile] = useState(false);

  const addRef = useRef();
  const limitRef = useRef();
  const scrolled = useScrolled(limitRef, scrolledBannerHeight);
  const currency = config?.currency;

  useEffect(() => {
    const resizeListener = () => {
      if (addRef.current) {
        if (scrolled) {
          const { height } = addRef.current.getBoundingClientRect();
          setScrolledHeight(height);
          setScrolledMobile(true);
        } else {
          setScrolledHeight(null);
        }
      }

      if (scrolledBannerRef && bannerRef?.current) {
        const { height } = bannerRef.current.getBoundingClientRect();
        setScrolledBannerHeight(height);
      } else {
        setScrolledBannerHeight(null);
      }
    };
    window.addEventListener("resize", resizeListener);
    resizeListener();
    return () => window.removeEventListener("resize", resizeListener);
  }, [scrolled, scrolledBannerRef]);

  const sectionsStyle =
    tabsHeight && scrolledHeight ? { top: -(tabsHeight + scrolledHeight) } : undefined;

  const loadProductDetails = useCallback(async () => {
    const { key_prod: productId, extraInfo } = babelProdotto;
    const normalizedProductId =
      babelConfig.domainsWithSap.includes(pathPrefix) && extraInfo?.ca4_CHG_HAIER_SAP
        ? extraInfo?.ca4_CHG_HAIER_SAP
        : productId;

    try {
      const [productDetail = {}] = await queryProductsDetails([normalizedProductId], currency);
      setPriceData((current) =>
        productDetail.availableForSale && productDetail.sellingPrice
          ? productDetail
          : current || null
      );
    } catch (err) {
      console.log("Error loading product details: ", err);
    }
  }, [babelProdotto, currency, pathPrefix]);

  const ecommerceActive =
    hasEcommerce &&
    [undefined, "[]"].includes(pageLine?.lineConfigurationFieldSet?.isNotEcommerce?.[0]);

  useEffect(() => {
    if (hasEcommerce) {
      loadProductDetails();
    }
  }, [loadProductDetails]);

  const bannerFeatures = useMemo(() => (featuresPdp ? featuresPdp : null), [featuresPdp]);

  const energyClassImg = useEnergyClassImg(energyClass, lineId, config);

  const [dataLayerAdded, setDatalayerAdded] = useState(false);
  useEffect(() => {
    if ((!hasEcommerce || priceData !== undefined) && !dataLayerAdded) {
      productDetailView(babelProdotto, priceData, origin, config);
      setDatalayerAdded(true);
    }
  }, [babelProdotto, priceData, dataLayerAdded, origin, config]);

  const jsonLdData = useMemo(
    () => generateMetadata(babelProdotto, priceData, config),
    [babelProdotto, priceData, config]
  );

  const indexAttributes = useMemo(() => {
    return Object.values(attributesMap)
      .map((item) =>
        item.fieldValue && item.fieldValue !== "-"
          ? {
              attrId: item.id,
              tagName: `tag-product-filter-${item.id}`,
              attrValue: item.fieldValue,
            }
          : null
      )
      .filter(Boolean);
  }, [attributesMap]);

  const campaignBoxesBannerPDP = useMemo(
    () => getCampaignBoxes(campaignsBannerPieDiPaginaPDP, priceData),
    [campaignsBannerPieDiPaginaPDP, priceData]
  );
  const resultBannerPDP = campaignBoxesBannerPDP.length
    ? campaignBoxesBannerPDP.concat(promoPdp)
    : promoPdp;

  const campaignBoxesBannerTop = useMemo(
    () => getCampaignBoxes(campaignsBannerTop, priceData),
    [campaignsBannerTop, priceData]
  );
  const resultBannerTop = campaignBoxesBannerTop.length ? campaignBoxesBannerTop : [];

  const readMoreDescription = productDescAfter || minicard4.length > (lineProductName2 ? 7 : 8);

  const campaignGlobalBoxesBannerTop = useMemo(
    () => getCampaignBoxes(campaignsGlobalBannerTop),
    [campaignsGlobalBannerTop]
  );
  const resultGlobalBannerTop = campaignGlobalBoxesBannerTop.length
    ? campaignGlobalBoxesBannerTop
    : [];

  const relatedPrice = related.some((prod) => prod.productSellingPrice !== null);

  const noRetailerPageCountryMap = new Map([
    ["/it_IT/", "haier.it"],
    ["/fr_FR/", "haier.fr"],
    ["/de_DE/", "haier.de"],
    ["/en_GB/", "haier.uk"],
    ["/pl_PL/", "haier.pl"],
    ["/nl_BE/", "haier.be"],
    ["/fr_BE/", "haier.be"],
    ["/nl_NL/", "haier.nl"],
    ["/bg_BG/", "haier.bg"],
    ["/fr_CH/", "haier.ch"],
    ["/de_CH/", "haier.ch"],
    ["/it_CH/", "haier.ch"],
    ["/es_ES/", "haier.es"],
    ["/el_GR/", "haier.gr"],
    ["/ru_RU/", "haier.ru"],
    ["/cs_CZ/", "haier.cz"],
    ["/sk_SK/", "haier.sk"],
    ["/tr_TR/", "haier.tr"],
    ["/pt_PT/", "haier.pt"],
    ["/sl_SI/", "haier.sl"],
    ["/hr_HR/", "haier.hr"],
    ["/en_IE/", "haier.ie"],
    ["/ro_RO/", "haier.ro"],
    ["/rs_SR_latin/", "haier.rs"],
    ["/hu_HU/", "haier.hu"],
    ["/uk_UA/", "haier.ua"],
    ["/no_NO/", "haier.no"],
    ["/da_DK/", "haier.dk"],
    ["/fi_FI/", "haier.fi"],
    ["/sv_SE/", "haier.se"],
    ["/lv_LV/", "haier.lv"],
    ["/lt_LT/", "haier.lt"],
    ["/et_EE/", "haier.ee"],
    ["/fr_MA/", "haier.ma"],
  ]);

  const noRetailerPage = pageLine?.lineConfigurationFieldSet?.noRetailerPage?.[0] || "";

  const checkLocatorCountry = (pathPrefix) => {
    if (pathPrefix === "/nl_BE/" || pathPrefix === "/nl_NL/") {
      return "nl";
    }
    if (pathPrefix === "/fr_BE/" || pathPrefix === "/fr_FR/" || pathPrefix === "/fr_CH/") {
      return "fr";
    }
    if (pathPrefix === "/de_DE/" || pathPrefix === "/de_CH/") {
      return "de";
    }
    if (pathPrefix === "/es_ES/") {
      return "es";
    }
    if (pathPrefix === "/it_IT/" || pathPrefix === "/it_CH/") {
      return "it";
    }
    if (pathPrefix === "/pl_PL/") {
      return "pl";
    }
    if (pathPrefix === "/pt_PT/") {
      return "pt";
    } else {
      return "en";
    }
  };

  const storeLocatorPageAC = `https://www.haierspares.eu/${checkLocatorCountry(
    pathPrefix
  )}/${productId}/?utm_source=${noRetailerPageCountryMap.get(
    pathPrefix
  )}&utm_medium=referral&utm_campaign=always_on_brand_website`;
  const storeLocatorPageCP = `https://www.premiumservicesforcareplusprotect.eu/${checkLocatorCountry(
    pathPrefix
  )}/${productId}/?utm_source=${noRetailerPageCountryMap.get(
    pathPrefix
  )}&utm_medium=referral&utm_campaign=always_on_brand_website`;

  const storeLink =
    noRetailerPage === "no retailer" && brandName === "Haier"
      ? storeLocatorPageAC
      : noRetailerPage === "no retailer" && brandName === "Care-Protect"
      ? storeLocatorPageCP
      : null;
  const imgTitle = `${productName} ${productId}`;

  const checkPdpConnectivityMessage =
    ((pathPrefix === "/en_GB/" && babelProdotto?.pdpConnectivity) ||
      (pathPrefix !== "/en_GB/" && priceData?.sellingPrice)) &&
    !!labelManagerLabels.pdpLegalSentence;

  useEkyScript(eanCode, productId);

  const pagePath = normalizeRelativePath(
    [pageLine.slug, productId, slug].map(safeSlugify).join("/")
  );

  const hasWifiManual = babelProdotto.manuals.find(
    (item) => item.lang === LANG && item.type === 50711 && item.filename?.startsWith("http")
  );

  return (
    <Layout
      bannerTop={resultBannerTop.length ? resultBannerTop[0] : resultGlobalBannerTop[0]}
      bannerRef={bannerRef}
      location={location}
      newMenu={!!isNewMenu}
    >
      <CompareProvider category={lineName}>
        <span className="tag-argument d-none">{labelManagerLabels.product}</span>
        <span className="tag-category d-none">{pageLine.liferayFields.title}</span>
        <span className="tag-product-id d-none">{productId}</span>
        <span className="tag-line-id d-none">{lineId}</span>
        <span className="tag-product-name d-none">{productName}</span>
        <span className="tag-product-code d-none">{productCode}</span>
        <span className="tag-product-category d-none">
          {pageLine.taxonomyCategoryBriefs?.[0]?.name}
        </span>
        {extraInfo?.ca4_CHG_HAIER_SAP && (
          <span className="tag-product-sapcode d-none">{extraInfo.ca4_CHG_HAIER_SAP}</span>
        )}
        {energyClass && (
          <span className="tag-energy-class d-none">{`energy_class:${energyClass}`}</span>
        )}
        {!!repairabilityIndex[0] && (
          <span className="tag-repairability-index d-none">{`repairability_index:${
            repairabilityIndex[0].product.find((index) => index.sku === productId).indexValue
          }`}</span>
        )}
        {indexAttributes.map((indexAttr) => (
          <span
            className={`${indexAttr.tagName} d-none`}
            key={indexAttr.attrId}
          >{`filterAttr:${indexAttr.attrValue}`}</span>
        ))}
        <JsonLd data={jsonLdData} />
        <div
          className="product-detail"
          style={{ "--scroll-tabs-height": tabsHeight ? `${tabsHeight}px` : null }}
        >
          {breadcrumb ? <Breadcrumb breadcrumb={breadcrumb.concat({ name: productCode })} /> : null}
          <BabelProdottoIntro
            babelProdotto={babelProdotto}
            enabledForLine={enabledForLine}
            energyClassImg={energyClassImg}
            config={config}
            productTitle={productTitle}
            lineProductName2={lineProductName2}
            readMoreDescription={readMoreDescription}
            location={location}
            labelManagerLabels={labelManagerLabels}
            siteMetadata={siteMetadata}
            liferayProductNotes={liferayProductNotes}
            priceData={priceData}
            compareProducts={compareProducts}
            storeLink={storeLink}
            bannerFeatures={
              hasEcommerce && priceData && bannerFeatures && !noRtl ? bannerFeatures : null
            }
            pagePath={pagePath}
          />
          <span ref={limitRef}></span>
          <ProductDetailTop
            ref={addRef}
            data={babelProdotto}
            productTitle={productTitle}
            priceData={priceData}
            scrolled={scrolled}
            labelManagerLabels={labelManagerLabels}
            siteMetadata={siteMetadata}
            location={location}
            config={config}
            //style={scrolledBannerHeight ? { top: scrolledBannerHeight } : undefined}
            productNote={liferayProductNotes}
            storeLink={storeLink}
            hasTabs={tabs?.length > 0}
          />

          {/* Scroll Tabs */}
          <ScrollTabs
            ref={tabsRef}
            tabs={tabs}
            activeTab={activeTab}
            sticky={true}
            style={scrolledHeight ? { top: scrolledHeight } : undefined}
            isProduct={true}
          />

          {/* Items Grid */}
          <span id="introduction" className="tabs__scroll-anchor" style={sectionsStyle} />
          <ItemsGrid
            data={itemsGrid}
            isProduct={true}
            showLogoImage={showLogoImage}
            showLogoSvg={showLogoSvg}
            className="py-0 mb-3"
          />

          {/* Technical specifications */}
          <span id="technical-spec" className="tabs__scroll-anchor" style={sectionsStyle} />
          <TechnicalSpecifications
            scrollRef={tabsRef}
            data={specificationsFiltered}
            hasWifiManual={hasWifiManual}
            labelManagerLabels={labelManagerLabels}
            otherDesign={babelProdotto?.otherDesign}
            otherFull={babelProdotto?.otherFull}
            imgTitle={imgTitle}
            checkPdpConnectivityMessage={checkPdpConnectivityMessage}
          />

          {/* Agec Regulation */}
          {categoriesFiltered.length > 0 && (
            <>
              <span id="agec-regulation" className="tabs__scroll-anchor" style={sectionsStyle} />
              <AgecRegulation
                scrollRef={tabsRef}
                data={categoriesFiltered}
                labelManagerLabels={labelManagerLabels}
                babelProdotto={babelProdotto}
              />
            </>
          )}

          {/* Documentation */}
          <span id="documentation" className="tabs__scroll-anchor" style={sectionsStyle} />
          <DocumentazioneProdotto
            data={babelProdotto}
            labelManagerLabels={labelManagerLabels}
            siteMetadata={siteMetadata}
            config={config}
            isProduct={true}
          />

          {productApp && <BannerPushApp data={productApp} position="pdp" />}

          {/* Slider prodotto correlati */}
          {!noRtl && related && related.length >= 3 && (
            <>
              <span id="related" className="tabs__scroll-anchor" style={sectionsStyle} />
              <SliderRelated
                data={related}
                ecommerceActive={ecommerceActive}
                compareProducts={compareProducts}
                config={config}
                labelManagerLabels={labelManagerLabels}
                location={location}
                productNote={liferayProductNotes}
                siteMetadata={siteMetadata}
                madeProductLineId={lineId}
              />
            </>
          )}

          {!!liferayProductNotes && (!!priceData || !!relatedPrice) && (
            <ProductNotes data={liferayProductNotes} position="plp" />
          )}

          {/* Reevoo */}
          {!!config.isReevooActiveFieldSet.isReevooActive?.length && (
            <>
              <span id="reviews" className="tabs__scroll-anchor" style={sectionsStyle} />
              <ReevooEmbeddable productId={productId} />
            </>
          )}

          <div className="product-detail__sticky-bottom-wrapper">
            <ProductDetailBottom
              scrolled={scrolled}
              scrolledMobile={scrolledMobile}
              data={babelProdotto}
              productTitle={productTitle}
              priceData={priceData}
              labelManagerLabels={labelManagerLabels}
              siteMetadata={siteMetadata}
              location={location}
              config={config}
              productNote={liferayProductNotes}
              storeLink={storeLink}
            />

            {!!resultBannerPDP?.length && (
              <BannerPushNotification sleekNoteId="sn-785b13d4-ee9e-4fc6-acfe-e7bcc50e61dd">
                <BannerPromo data={resultBannerPDP} />
              </BannerPushNotification>
            )}
          </div>

          <CompareBar
            to={`${normalizeRelativePath(lineName)}compare/`}
            products={compareProducts}
            config={config}
          />
        </div>
      </CompareProvider>
      {/* {config.virtualRealityActive &&
        config.virtualRealityActive.value !== "[]" &&
        <script
          type="text/plain"
          className="optanon-category-C0003"
          src="//media.flixfacts.com/js/loader.js"
          data-flix-distributor={
            config.virtualRealityActive.content.virtualRealityDistributorLang[0].content
              .virtualRealityDistributorCode.value
          }
          data-flix-language={
            config.virtualRealityActive.content.virtualRealityDistributorLang[0].value
          }
          data-flix-mpn={productName.replace(/-/g, "")}
          data-flix-sku={productId}
          data-flix-ean={eanCode}
          data-flix-hotspot="true"
          data-flix-3d="flix-3dfw"
          data-flix-autoload="hotspot"
          async={true}
        />
      } */}
    </Layout>
  );
};

export const Head = ({ data: { babelProdotto, site } }) => {
  const {
    key_prod: productId,
    productName,
    primaryimage,
    htmlTitle,
    slug,
    eanCode,
    familiesIds,
    pageLine,
    minicard4,
    productDescBefore,
    productDescAfter,
    productCode,
  } = babelProdotto;
  const productFamily = familiesIds && familiesIds.length ? familiesIds[0] : null;
  const pageLineNames = pageLine?.pageName;
  const lineProductName2 = pageLineNames && pageLineNames[1] ? pageLineNames[1] : "";
  const miniCardData = lineProductName2 ? [lineProductName2].concat(minicard4) : minicard4;
  const metaDescription =
    productCode +
    " | " +
    (productDescBefore ? productDescBefore : miniCardData.slice(0, 8).join(" ")) +
    " " +
    (productDescAfter ? productDescAfter : miniCardData.slice(8).join(" "));

  const ogImage = useMemo(() => {
    const primary = primaryimage.find((img) => img.imageType === "640");
    return primary ? `${site.siteMetadata.siteUrl}${primary.resize.src}` : placeholderLogoImg;
  }, [primaryimage, site.siteMetadata.siteUrl]);

  const itempropMeta = useMemo(
    () => [
      {
        itemprop: "name",
        content: productName,
      },
      {
        itemprop: "brand",
        content: "HAIER",
      },
      {
        itemprop: "sku",
        content: productId,
      },
      {
        itemprop: "description",
        content: productFamily ? productFamily.text : metaDescription,
      },
      {
        itemprop: "image",
        content: ogImage,
      },
      {
        itemprop: "mnp",
        content: eanCode,
      },
    ],
    [productName, productId, productFamily, metaDescription, ogImage, eanCode]
  );

  const productPath = [pageLine.slug, productId, slug].map(safeSlugify).join("/");

  return (
    <Seo
      title={htmlTitle}
      description={metaDescription}
      path={productPath}
      ogImage={ogImage}
      meta={itempropMeta}
    />
  );
};

export default ProductDetailTemplate;
export const query = graphql`
  fragment BabelProdottoDetailFragment on BabelProdotto {
    brandName
    key_prod
    slug
    lineId
    productName
    productNameField
    productCode
    energyLabelCav
    energyClass
    eanCode
    eanCodeField
    assetId
    htmlTitle
    productTitleNoFamilyAC
    productVideo
    productDescBefore
    productDescAfter
    agecCategories {
      title
      data {
        id
        id_option
        fieldName
        fieldValue
        fieldMultiValue
        fieldBoolean
      }
    }
    pdpConnectivity
    extraServices {
      title
      description
      service_id
    }
    dimensions {
      l
      p
      a
    }
    dimensionsPack {
      l
      p
      a
    }
    extraInfo {
      ca4_CHG_HAIER_SAP
    }
    attributesMap
    productTags {
      id
      productTag
      color
      colorHexa
      colorHexaText
      colorCategory
      position
      liferayFields {
        taxonomyCategoryBriefs {
          name
        }
      }
    }
    bannerNotification {
      ...BannerNotificationFragment
    }
    badgePdp {
      ...ProductBadgePDPFragment
    }
    campaignBadge: campaigns(positions: ["PDP | Badge"]) {
      filterField {
        pmInStock
        pmSpecialPrice
      }
      date {
        pmEnd
        pmStart
      }
      boxAndPosition {
        pmBoxPosition
        webContent {
          ...ProductBadgePDPFragment
        }
      }
    }
    campaignsCard: campaigns(positions: ["CARD | Tag Prodotto"]) {
      filterField {
        pmInStock
        pmSpecialPrice
      }
      date {
        pmEnd
        pmStart
      }
      boxAndPosition {
        pmBoxPosition
        webContent {
          ... on LiferayProductTag {
            id
            productTag
            color
            colorHexa
            colorHexaText
            colorCategory
            position
            liferayFields {
              taxonomyCategoryBriefs {
                name
              }
            }
          }
        }
      }
      tagActive
    }
    campaignsPDP: campaigns(positions: ["PDP | Banner Promo"]) {
      filterField {
        pmInStock
        pmSpecialPrice
      }
      date {
        pmEnd
        pmStart
      }
      boxAndPosition {
        pmBoxPosition
        webContent {
          ...BannerPromoCMFragment
        }
      }
    }
    campaignsBannerPieDiPaginaPDP: campaigns(positions: ["PDP | Banner Pie di Pagina"]) {
      date {
        pmStart
        pmEnd
      }
      filterField {
        pmInStock
        pmSpecialPrice
      }
      boxAndPosition {
        pmBoxPosition
        webContent {
          ... on LiferayPromoPdp {
            id
            liferayFields {
              siteId
              articleId
            }
            promoExternalTitleFieldSet {
              promoExternalTitle
              promoType
              promoInternalTitle
              promoDescription
              promoCtaLabelFieldSet {
                promoCtaLabel
                promoCtaLink
              }
              promoSkus
              skusText
            }
          }
        }
      }
    }
    campaignsBannerTop: campaigns(positions: ["BANNER TOP | Banner Promo"]) {
      filterField {
        pmInStock
        pmSpecialPrice
      }
      date {
        pmEnd
        pmStart
      }
      boxAndPosition {
        pmBoxPosition
        webContent {
          ...BannerTopMenuFragment
        }
      }
    }
    familiesIds {
      title
      text
      image
      imageAdaptive {
        gatsbyImageData(width: 800, layout: CONSTRAINED)
      }
      imageAdaptiveFull: imageAdaptive {
        gatsbyImageData(layout: FULL_WIDTH)
      }
      titleimg
      video
      linkcall
      triples {
        id
        title
        text
        keySellingPoint
        image
        imageAdaptive {
          gatsbyImageData(width: 800, layout: CONSTRAINED)
        }
        imageAdaptiveFull: imageAdaptive {
          gatsbyImageData(layout: FULL_WIDTH)
        }
        titimg
        video
        order
        logoSvg
        linkcall
      }
      logo_image
    }
    triples {
      id
      title
      text
      keySellingPoint
      image
      imageAdaptive {
        gatsbyImageData(width: 800, layout: CONSTRAINED)
      }
      imageAdaptiveFull: imageAdaptive {
        gatsbyImageData(layout: FULL_WIDTH)
      }
      titimg
      video
      order
      logoSvg
      linkcall
    }
    primaryThumb: primaryimage {
      imageType
      gatsbyImageData(width: 85, formats: [AUTO, WEBP])
    }
    primaryMedium: primaryimage {
      imageType
      gatsbyImageData(width: 640)
    }
    primaryFull: primaryimage {
      imageType
      gatsbyImageData(layout: FULL_WIDTH)
    }
    s3Thumb: s3Images {
      imageType
      gatsbyImageData(width: 85, formats: [AUTO, WEBP])
    }
    s3Medium: s3Images {
      imageType
      gatsbyImageData(width: 640)
    }
    s3Full: s3Images {
      imageType
      gatsbyImageData(layout: FULL_WIDTH)
    }
    otherThumb: otherImages {
      imageType
      imageUrl
      gatsbyImageData(width: 85, formats: [AUTO, WEBP])
    }
    otherMedium: otherImages {
      imageType
      imageUrl
      gatsbyImageData(width: 640)
    }
    otherFull: otherImages {
      imageType
      imageUrl
      gatsbyImageData(layout: FULL_WIDTH)
    }
    otherDesign: otherImages {
      imageType
      imageUrl
      gatsbyImageData(width: 100)
    }
    primaryimage {
      imageType
      imageUrl
      resize(width: 1200) {
        src
      }
    }
    s3Images {
      imageType
      imageUrl
    }
    otherImages {
      imageType
      imageUrl
    }
    specifications
    manuals {
      filename
      lang
      type
    }
    urlCertification
    minicard2
    minicard4
    productMsrpPrice
    productSellingPrice
    productOmnibusPrice
    productEcommerceMsrpPrice
    productEcommerceSellingPrice
    productEcommerceOmnibusPrice
    ratingValue
    reviewCount
    url360
    shopLink
    repairabilityIndex {
      ...RepairabilityIndex
    }
    pageLine {
      liferayFields {
        title
      }
      slug
      liferayFields {
        taxonomyCategoryBriefs {
          name
        }
      }
      parentPages {
        page {
          ... on LiferayPage {
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageLine {
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageCrossLine {
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageExternalLink {
            liferayFields {
              title
            }
            externalLink
          }
        }
        menuItem {
          name
          useCustomName
        }
      }
      pageName
      descriptionBefore
      descriptionAfter
      lineConfigurationFieldSet {
        isProductSheet
        catalogueLinkFieldSet {
          catalogueLink
          isCatalogue
        }
        isNewEnergyLabelEu
        isNewEnergyLabelUk
        isDeclarationOfConformity
        isDeclarationOfConformityUk
        isFicheDeProduit
        isNewFicheDeProduit
        isNotEcommerce
        noRetailerPage
      }
      allBabelProdotto {
        key_prod
        productTitleNoFamilyAC
        productCode
        lineId
        primaryimage {
          imageType
          imageUrl
        }
        familiesIds {
          title
        }
        pageLine {
          pageName
        }
      }

      comparePage {
        slug
      }
    }
    declarationOfConformity {
      uk
      eu
    }
    promoPdp {
      id
      liferayFields {
        siteId
        articleId
      }
      promoExternalTitleFieldSet {
        promoExternalTitle
        promoType
        promoInternalTitle
        promoDescription
        promoCtaLabelFieldSet {
          promoCtaLabel
          promoCtaLink
        }
        promoSkus
        skusText
      }
    }
    productApp {
      ...ProductAppFragment
    }
    featuresPdp {
      titleFieldSet {
        title
        icon {
          node {
            publicURL
          }
        }
        customName
        linkText
        abstract
        link
      }
    }
  }
  query ($id: String) {
    babelProdotto(id: { eq: $id }) {
      ...BabelProdottoDetailFragment
      related {
        ...BabelProdottoFragment
      }
    }
    agecRegulation: liferayAgecRegulation {
      title
      fieldsGroup {
        sectionTitle
        attributes
      }
      anchorDesktop
      anchorMobile
    }
    config: liferayGeneralConfiguration {
      haierStEcommLines {
        productLine
      }
      isReevooActiveFieldSet {
        isReevooActive
      }
      priceInPerc: priceInPerc
      wtbAndRetailersActive
      currency
      tagActive
      tagColor
      invertProductFamily
    }
    liferayProductNotes {
      ...ProductNotesFragment
    }
    labelManagerLabels {
      home
      product
      introduction
      wifi50711Manual
      manualType50711
      manualType50712
      technicalSpecifications
      relatedProducts
      reviews
      documents
      instructionsManual
      installationManual
      productSheet
      catalogue
      energyLabelUk
      newEnergyLabel
      energyLabelEl
      energyLabelCavity
      ficheDeProduit
      newFicheDeProduit
      declarationOfConformity
      declarationOfConformityUk
      includedService
      readMore
      readLess
      compare
      removeFromComparison
      addToCart
      whereToBuy
      showProduct
      discoverMore
      buyNow
      seeAllTechnicalSpec
      savings
      video
      activateReevoo
      shippingCosts
      pricePercentageTooltip
      priceAbsoluteTooltip
      yes
      no
      buyOnHaierShop
      close
      productPreview
      goToProductPage
      seeSizeDiagram
      pdpLegalSentence
      additionalServices
      galleryAll
      galleryVideo
      gallery360
      galleryDimensions
      cookieGalleryButton
      cookieGalleryButtonManage
    }
    site {
      siteMetadata {
        priceLanguage
        babelPrefix
        derivedLang
        siteUrl
      }
    }
    campaignsGlobalBannerTop: globalCampaigns(positions: ["BANNER TOP | Banner Promo"]) {
      date {
        pmStart
        pmEnd
      }
      filterField {
        pmInStock
        pmSpecialPrice
      }
      boxAndPosition {
        pmBoxPosition
        webContent {
          ...BannerTopMenuFragment
        }
      }
    }
    newMainMenuFiltered {
      name
    }
    liferayMenuConfiguration {
      newMenuActive
      copyrightText
    }
  }
`;
